import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Volume2, Shield, Database, Users, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-600 p-2 rounded-lg">
              <Volume2 className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Speakify</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="h-8 w-8 text-indigo-600" />
              <CardTitle className="text-3xl text-gray-900">Privacy Policy</CardTitle>
            </div>
            <p className="text-gray-600">Last Updated: January 20, 2026</p>
          </CardHeader>

          <CardContent className="space-y-8 text-gray-700">
            {/* Introduction */}
            <section>
              <p className="text-lg leading-relaxed">
                Speakify (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Shopify application.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <div className="flex items-center space-x-2 mb-3">
                <Database className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-semibold text-gray-900">Information We Collect</h2>
              </div>
              
              <div className="space-y-4 ml-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Store Information</h3>
                  <p className="leading-relaxed">
                    When you install Speakify, we collect and store:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Your Shopify store domain</li>
                    <li>Store access token (for API access)</li>
                    <li>App installation timestamp</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Product Data</h3>
                  <p className="leading-relaxed">
                    To generate audio descriptions, we access:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Product titles and descriptions</li>
                    <li>Product handles (URLs)</li>
                    <li>Product IDs</li>
                    <li>Featured product images</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Generated Content</h3>
                  <p className="leading-relaxed">
                    We store:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Generated audio files (stored on AWS S3)</li>
                    <li>Voice preferences and settings</li>
                    <li>Custom voice samples (if you use voice cloning)</li>
                    <li>Text content used for audio generation</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">4. Analytics Data</h3>
                  <p className="leading-relaxed">
                    To provide merchants with insights into audio performance, we collect:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Audio interaction events (plays, completions, cart additions)</li>
                    <li>Session IDs (anonymous user tracking)</li>
                    <li>Device type information</li>
                    <li>Product handles associated with audio plays</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Processing of Customer Personal Information */}
            <section>
              <div className="flex items-center space-x-2 mb-3">
                <Users className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-semibold text-gray-900">Processing of Customer Personal Information</h2>
              </div>
              
              <div className="space-y-4 ml-8">
                <p className="leading-relaxed">
                  To provide merchants with insights into the effectiveness of audio content, Speakify processes limited Customer Personal Identifiable Information (PII) obtained via Shopify Webhooks (specifically <code className="bg-gray-100 px-2 py-1 rounded text-sm">orders/create</code>).
                </p>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Collected</h3>
                  <p className="leading-relaxed">
                    We may process <strong>Customer Name</strong> and <strong>Email Address</strong> from completed orders.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Purpose</h3>
                  <p className="leading-relaxed">
                    This data is used solely to attribute a successful purchase to a specific interaction with the Speakify audio player on the storefront. This allows merchants to understand the Return on Investment (ROI) of their audio content.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Minimization</h3>
                  <p className="leading-relaxed">
                    We do not collect or store:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Sensitive payment information</li>
                    <li>Physical addresses (unless required for specific reporting)</li>
                    <li>Phone numbers</li>
                    <li>Social security numbers or government IDs</li>
                  </ul>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-green-900 mb-2">Non-Disclosure</h3>
                  <p className="leading-relaxed text-green-800">
                    Customer PII is <strong>never sold, shared with third parties, or used for marketing by Speakify</strong>. It is displayed only to the merchant who installed the app for the purpose of ROI analytics.
                  </p>
                </div>
              </div>
            </section>

            {/* How We Use Information */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">How We Use Your Information</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">We use the collected information to:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Provide Services:</strong> Generate AI-powered audio descriptions for your products</li>
                  <li><strong>Store Audio Files:</strong> Save generated audio on AWS S3 for delivery to your customers</li>
                  <li><strong>Voice Cloning:</strong> Create and manage custom voice clones using ElevenLabs API</li>
                  <li><strong>Text Enhancement:</strong> Improve product descriptions using Claude AI (Anthropic)</li>
                  <li><strong>Analytics & ROI Tracking:</strong> Track audio performance and attribute purchases to audio interactions to help merchants understand content effectiveness</li>
                  <li><strong>App Functionality:</strong> Authenticate your store and manage app settings</li>
                  <li><strong>Support:</strong> Troubleshoot issues and provide customer support</li>
                </ul>
              </div>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Third-Party Services</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">Speakify integrates with the following third-party services:</p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-1">ElevenLabs (Text-to-Speech)</h3>
                    <p className="text-sm">Used for: Generating audio from text and voice cloning</p>
                    <p className="text-sm mt-1">Privacy Policy: <a href="https://elevenlabs.io/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">https://elevenlabs.io/privacy</a></p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-1">Anthropic Claude (AI Text Enhancement)</h3>
                    <p className="text-sm">Used for: Improving product descriptions for better audio narration</p>
                    <p className="text-sm mt-1">Privacy Policy: <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">https://www.anthropic.com/legal/privacy</a></p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-1">Amazon Web Services (AWS S3)</h3>
                    <p className="text-sm">Used for: Storing generated audio files and voice samples</p>
                    <p className="text-sm mt-1">Privacy Policy: <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">https://aws.amazon.com/privacy/</a></p>
                  </div>
                </div>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <div className="flex items-center space-x-2 mb-3">
                <Shield className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-semibold text-gray-900">Data Security</h2>
              </div>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">We implement appropriate technical and organizational measures to protect your data:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Encryption:</strong> All data is transmitted over HTTPS</li>
                  <li><strong>Access Control:</strong> Store access tokens are securely encrypted</li>
                  <li><strong>HMAC Verification:</strong> All webhooks are verified for authenticity</li>
                  <li><strong>Secure Storage:</strong> Audio files are stored on AWS S3 with appropriate access controls</li>
                  <li><strong>Limited Access:</strong> Only authorized personnel can access stored data</li>
                </ul>
              </div>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Data Retention and Deletion</h2>
              
              <div className="space-y-3 ml-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">While App is Installed</h3>
                  <p className="leading-relaxed">
                    We retain your store data, product information, and generated audio files as long as Speakify is installed on your store.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">After Uninstallation</h3>
                  <p className="leading-relaxed">
                    When you uninstall Speakify:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Your app is immediately marked as uninstalled</li>
                    <li>After 48 hours, ALL your data is permanently deleted, including:
                      <ul className="list-circle ml-6 mt-1 space-y-1">
                        <li>Store records and access tokens</li>
                        <li>All generated audio files from S3</li>
                        <li>All custom voice samples</li>
                        <li>Custom voice clones from ElevenLabs</li>
                        <li>Product audio metadata</li>
                        <li>All customer PII collected for analytics (names, emails)</li>
                        <li>All analytics events and data</li>
                      </ul>
                    </li>
                  </ul>
                  <p className="leading-relaxed mt-2 text-sm text-gray-600">
                    The 48-hour delay is mandated by Shopify&apos;s GDPR compliance requirements.
                  </p>
                </div>
              </div>
            </section>

            {/* Your Rights (GDPR/CCPA) */}
            <section>
              <div className="flex items-center space-x-2 mb-3">
                <Users className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-semibold text-gray-900">Your Rights</h2>
              </div>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">Under GDPR (EU) and CCPA (California), you have the right to:</p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your data</li>
                  <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                  <li><strong>Erasure:</strong> Request deletion of your data (by uninstalling the app)</li>
                  <li><strong>Portability:</strong> Request your data in a portable format</li>
                  <li><strong>Object:</strong> Object to processing of your data</li>
                </ul>
                <p className="leading-relaxed">
                  To exercise these rights or if you have questions, contact us at: <a href="mailto:question2abhi@gmail.com" className="text-indigo-600 hover:underline">question2abhi@gmail.com</a>
                </p>
              </div>
            </section>

            {/* GDPR Compliance Webhooks */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">GDPR Compliance</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  Speakify is fully compliant with Shopify&apos;s GDPR requirements. We have implemented the following mandatory webhooks:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Customer Data Request:</strong> Merchants can request customer data on behalf of their customers. We will provide all stored customer PII (name, email) associated with order attribution for analytics purposes.</li>
                  <li><strong>Customer Data Deletion:</strong> Merchants can request customer data deletion. We will permanently delete all customer PII associated with the specified customer within 48 hours.</li>
                  <li><strong>Shop Data Deletion:</strong> After 48 hours of app uninstallation, all shop data, including all customer PII collected for analytics, is automatically and permanently deleted.</li>
                </ul>
              </div>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Cookies and Tracking</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  Speakify uses minimal cookies:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Authentication:</strong> Session tokens to authenticate your store (required for app functionality)</li>
                  <li><strong>App Bridge:</strong> Shopify&apos;s App Bridge uses cookies for embedded app functionality</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  We do NOT use tracking cookies, analytics cookies, or advertising cookies.
                </p>
              </div>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Children&apos;s Privacy</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  Speakify is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
                </p>
              </div>
            </section>

            {/* Changes to Privacy Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">Changes to This Privacy Policy</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Updating the &quot;Last Updated&quot; date at the top of this policy</li>
                  <li>Sending a notification through the app (for significant changes)</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Your continued use of Speakify after any changes constitutes acceptance of the updated policy.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="bg-indigo-50 p-6 rounded-lg border border-indigo-100">
              <div className="flex items-center space-x-2 mb-3">
                <Mail className="h-6 w-6 text-indigo-600" />
                <h2 className="text-2xl font-semibold text-gray-900">Contact Us</h2>
              </div>
              
              <div className="space-y-2 ml-8">
                <p className="leading-relaxed">
                  If you have questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="mt-4 space-y-1">
                  <p><strong>Email:</strong> <a href="mailto:question2abhi@gmail.com" className="text-indigo-600 hover:underline">question2abhi@gmail.com</a></p>
                  <p><strong>App:</strong> Speakify for Shopify</p>
                  <p><strong>Developer:</strong> Vortex AI Advisory</p>
                </div>
              </div>
            </section>

            {/* Footer */}
            <section className="text-center pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                By using Speakify, you acknowledge that you have read and understood this Privacy Policy.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
